import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BreakPoints, PlatformService } from '@shared/services/platform.service';
import { LanguageService } from '@shared/shared/services/language-service';
import { Observable, Subscription, combineLatest } from 'rxjs';
import {
  NOTIFICATION_DATA,
  NotificationComponent,
  NotificationData,
} from '../components/notification/notification.component';

const DISPLAY_TIMEOUT = 3000;
const TOP_POSITION = '130px';
const SIDE_POSITION = '20px';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private timer: ReturnType<typeof setTimeout>;
  private mobile$: Observable<boolean>;

  private overlayRef: OverlayRef;
  private overlayUpdater: Subscription;

  constructor(
    private overlay: Overlay,
    private languageService: LanguageService,
    private translateService: TranslateService,
    private liveAnnouncer: LiveAnnouncer,
    private platformService: PlatformService
  ) {
    this.mobile$ = this.platformService.breakPointChange$(BreakPoints.MOBILE);
  }

  public showNotification(
    translationKey: string,
    params?: { [key: string]: string },
    ariaLabelTranslationKey?: string
  ) {
    if (this.timer) {
      clearTimeout(this.timer);
      this.cleanup();
    }

    const notificationData: NotificationData = { translationKey, params };
    const injector = Injector.create({ providers: [{ provide: NOTIFICATION_DATA, useValue: notificationData }] });
    const portal = new ComponentPortal(NotificationComponent, null, injector);
    this.overlayRef = this.overlay.create({
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
    });
    this.overlayRef.attach(portal);
    this.overlayUpdater = this.getOverlayUpdater();
    this.timer = setTimeout(() => this.cleanup(), DISPLAY_TIMEOUT);

    if (ariaLabelTranslationKey) {
      const ariaLabel = this.translateService.instant(ariaLabelTranslationKey);
      this.liveAnnouncer.announce(ariaLabel);
    }
  }

  private getOverlayUpdater(): Subscription {
    return combineLatest([this.mobile$, this.languageService.isRtlLanguage$]).subscribe(([mobile, rtlLanguage]) => {
      let positionStrategy = this.overlay.position().global().top(TOP_POSITION);

      if (mobile) {
        positionStrategy = positionStrategy.centerHorizontally();
      } else {
        positionStrategy = rtlLanguage ? positionStrategy.left(SIDE_POSITION) : positionStrategy.right(SIDE_POSITION);
      }

      this.overlayRef.updatePositionStrategy(positionStrategy);
      this.overlayRef.updateSize(mobile ? { maxWidth: '96vw' } : null);
    });
  }

  private cleanup() {
    this.overlayRef.dispose();
    this.overlayRef = undefined;
    this.timer = undefined;
    this.overlayUpdater.unsubscribe();
  }
}
